import React from 'react'
import lodash from 'lodash'
import API from '../api/fetchAPI'
import LargeSpinnerOverlay from '../components/util/LargeSpinnerOverlay'
import { Icon as IconifyIcon } from "@iconify/react"
import {
    Franklin as FranklinLink,
    Gnomad as GnomadLink,
    Varsome as VarsomeLink,
    ClinVar as ClinVarLink
} from '../components/linkout/Linkout'
import './CaseView.scss'

const valueToCSS = value => {
    if(lodash.isString(value)) {
        return value.replace(/[^A-Za-z0-9]/g, '');
    } else if(value != null) {
        return value
    } else {
        return 'null'
    }
}

const renderValue = (value) => {
    if (value == null) {
        return <div className='value empty'>-</div>
    } else if (lodash.isArray(value)) {
        return <div className='value'>{value.map((item, idx) => <div key={idx} className={`item VALUE-${valueToCSS(item)}`}>{item}</div>)}</div>
    } else if (lodash.isObject(value) && !lodash.isDate(value)) {
        return <div className='value'>{JSON.stringify(value)}</div>
    } else if (lodash.isDate(value) || (lodash.isString(value) && /^\d{4}-\d{2}-\d{2}/.test(value))) {
        return <div className={`value VALUE-${valueToCSS(value)}`}>{new Date(value).toLocaleDateString()}</div>
    } else {
        return <div className={`value VALUE-${valueToCSS(value)}`}>{value}</div>
    }
}

function CaseView({ row }) {
    const caseId = React.useMemo(() => row?.id, [row])
    const [caseData, setCaseData] = React.useState(null)
    const [isLoading, setIsLoading] = React.useState(true)

    const loadCaseData = React.useCallback(() => {
        if (caseId == null || lodash.isString(caseId) === false || caseId.length <= 0) {
            return
        }

        setIsLoading(true)

        API.get(`/api/grid/cases/${caseId}`, { doNotThrowFor: [404] }).then(response => {
            setCaseData(response)
            setIsLoading(false)
        }).catch(e => {
            console.error(`Could not load case data for id '${caseId}'`)
            console.error(e)
            // Use the row data as fallback
            setCaseData(row.data)
            setIsLoading(false)
        })
    }, [caseId, row.data])

    React.useEffect(() => {
        loadCaseData()
    }, [loadCaseData])

    const renderCase = () => {
        // Use caseData if available, otherwise fall back to row.data
        const data = caseData || row.data

        return (
            <>
                {data.clinvarImport && <div className="section">
                    <strong>{data.clinvarPlaceholder ? 'ClinVar-Platzhalter' : 'Aus ClinVar-Platzhalter ergänzter Patientenfall'}</strong>
                    <p>{data.clinvarImport.technicalId}</p>
                    {data.clinvarPlaceholder && <p>Keine bestätigten Patientendaten. Ergänzung und ausdrückliche Zuordnung sind in der Variantenansicht möglich.</p>}
                    {data.clinvarImport.linkedCaseId && <p>Verknüpfter Fall: {data.clinvarImport.linkedCaseId}</p>}
                </div>}
                <div className="section-row">
                    <div className="section">
                        <div className="section-title">Identifikation</div>

                        <div className="label-value-row">
                            <div className="label">Case ID</div>
                            {renderValue(caseId)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">Internal Case ID</div>
                            {renderValue(data.internalCaseId)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">Sequencing Lab</div>
                            {renderValue(data.sequencingLab?.shortName || data.sequencingLab)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">External Case ID</div>
                            {renderValue(data.externalCaseId)}
                        </div>
                    </div>

                    <div className="section">
                        <div className="section-title">Patient</div>

                        <div className="label-value-row">
                            <div className="label">Sex</div>
                            {renderValue(data.sex)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">Age (months)</div>
                            {renderValue(data.ageInMonths)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">Age (years)</div>
                            {renderValue(data.ageinYears)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">Date of Birth</div>
                            {renderValue(data.dateOfBirth)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">Prenatal</div>
                            {renderValue(data.prenatal)}
                        </div>
                    </div>

                    <div className="section">
                        <div className="section-title">Diagnostik</div>

                        <div className="label-value-row">
                            <div className="label">Case Status</div>
                            {renderValue(data.caseStatus)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">Disease Category</div>
                            {renderValue(data.diseaseCategory)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">Test Conducted</div>
                            {renderValue(data.testConducted)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">Single/Duo/Trio</div>
                            {renderValue(data.singleDuoTrio)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">Start der Diagnostik</div>
                            {renderValue(data.startDerDiagnostik)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">Befunddatum</div>
                            {renderValue(data.befunddatum)}
                        </div>
                    </div>
                </div>

                <div className="section-row">
                    <div className="section">
                        <div className="section-title">Externe IDs</div>

                        <div className="label-value-row">
                            <div className="label">GestaltMatcher ID</div>
                            {renderValue(data.gestaltMatcherId)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">Face2Gene ID</div>
                            {renderValue(data.face2GeneId)}
                        </div>
                    </div>

                    <div className="section">
                        <div className="section-title">HPO Terms</div>

                        <div className="label-value-row">
                            <div className="label">HPO Terms</div>
                            {renderValue(data.hpoTerms)}
                        </div>
                    </div>

                    <div className="section">
                        <div className="section-title">Additional Info</div>

                        <div className="label-value-row">
                            <div className="label">Referring Clinician</div>
                            {renderValue(data.referringClinician)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">Selektivvertrag</div>
                            {renderValue(data.selektivvertrag)}
                        </div>

                        <div className="label-value-row">
                            <div className="label">Autozygosity</div>
                            {renderValue(data.autozygosity)}
                        </div>
                    </div>
                </div>

                {data.variants && lodash.isArray(data.variants) && data.variants.length > 0 && (
                    <div className="section">
                        <div className="section-title">Variants ({data.variants.length})</div>
                        <div className="variants-list">
                            {data.variants.map((variant, idx) => {
                                const variantRef = variant.variant?.reference
                                return (
                                    <div key={idx} className="variant-item box">
                                        <div className="variant-header">
                                            <span className="gene-name">{variant.gene}</span>
                                            <span className={`variant-role VALUE-${valueToCSS(variant.variantSolvesCase)}`}>
                                                {variant.variantSolvesCase}
                                            </span>
                                        </div>

                                        <div className="variant-row">
                                            {/* Genomic Info */}
                                            <div className="variant-column">
                                                <div className="label-value-row">
                                                    <div className="label">HGVS cDNA</div>
                                                    {renderValue(variant.variant?.transcript)}
                                                </div>
                                                {variantRef?.GRCh38?.gDNA && (
                                                    <div className="label-value-row">
                                                        <div className="label">HGVS gDNA (GRCh38)</div>
                                                        {renderValue(variantRef.GRCh38.gDNA)}
                                                    </div>
                                                )}
                                                {variantRef?.GRCh38 && (
                                                    <div className="label-value-row">
                                                        <div className="label">Position (GRCh38)</div>
                                                        <div className="value genomic-position">
                                                            <span>chr{variantRef.GRCh38.chr}:</span>
                                                            <span>{variantRef.GRCh38.pos}</span>
                                                            <span className="ref-alt">{variantRef.GRCh38.ref}{'>'}{variantRef.GRCh38.alt}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Classification */}
                                            <div className="variant-column">
                                                <div className="label-value-row">
                                                    <div className="label">ACMG Class</div>
                                                    {renderValue(variant.acmg?.class)}
                                                </div>
                                                <div className="label-value-row">
                                                    <div className="label">ACMG Criteria</div>
                                                    {renderValue(variant.acmg?.criteria)}
                                                </div>
                                                <div className="label-value-row">
                                                    <div className="label">Zygosity</div>
                                                    {renderValue(variant.zygosity)}
                                                </div>
                                                <div className="label-value-row">
                                                    <div className="label">Inheritance</div>
                                                    {renderValue(variant.modeOfInheritance)}
                                                </div>
                                            </div>

                                            {/* Additional Info */}
                                            <div className="variant-column">
                                                <div className="label-value-row">
                                                    <div className="label">Segregation</div>
                                                    {renderValue(variant.segregationsanalyse)}
                                                </div>
                                                {variant.pubMedId && (
                                                    <div className="label-value-row">
                                                        <div className="label">PubMed ID</div>
                                                        {renderValue(variant.pubMedId)}
                                                    </div>
                                                )}
                                                {variant.clinvarAccessionId && (
                                                    <div className="label-value-row">
                                                        <div className="label">ClinVar ID</div>
                                                        {renderValue(variant.clinvarAccessionId)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* External Links */}
                                        {variantRef && (
                                            <div className="variant-links">
                                                <span className="links-label">External Links:</span>
                                                <div className="links">
                                                    {variantRef.GRCh38 && <ClinVarLink value={variantRef.GRCh38} />}
                                                    {variantRef.GRCh37 && <FranklinLink value={variantRef.GRCh37} />}
                                                    {variantRef.GRCh38 && <GnomadLink value={variantRef.GRCh38} />}
                                                    {variantRef.GRCh38 && <VarsomeLink value={variantRef.GRCh38} />}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                {(data.kommentar || data.bisherigeDiagnostik || data.changesInManagementOrTherapyAfterTest) && (
                    <div className="section">
                        <div className="section-title">Comments & Notes</div>

                        {data.bisherigeDiagnostik && (
                            <div className="label-value-row">
                                <div className="label">Bisherige Diagnostik</div>
                                {renderValue(data.bisherigeDiagnostik)}
                            </div>
                        )}

                        {data.changesInManagementOrTherapyAfterTest && (
                            <div className="label-value-row">
                                <div className="label">Changes in Management/Therapy</div>
                                {renderValue(data.changesInManagementOrTherapyAfterTest)}
                            </div>
                        )}

                        {data.kommentar && (
                            <div className="label-value-row">
                                <div className="label">Kommentar</div>
                                {renderValue(data.kommentar)}
                            </div>
                        )}
                    </div>
                )}
            </>
        )
    }

    return (
        <div className="case-view">
            {isLoading === true ? (
                <LargeSpinnerOverlay label="loading..." />
            ) : (
                renderCase()
            )}
        </div>
    )
}

export default CaseView
